import { ILesson, ISection, TIME_HOURS_LS, TIME_MINUTE_LS } from "../type";
import { logClientDiagnostic } from "./clientDiagnosticLog";

type PrefetchPayload = {
  type: "WW_PREFETCH_URLS";
  urls: string[];
};

const PREFETCH_SW_RESULT_TYPE = "WW_PREFETCH_RESULT";
/** Ждём ответ SW дольше 8s: prefetch медиа + ответ через channel/clients. */
const PREFETCH_SW_WAIT_MS = 15_000;

type SwPrefetchWaiter = {
  promise: Promise<Record<string, unknown> | null>;
  attachReplyPort: (port: MessagePort) => void;
};

function isPrefetchResultMessage(data: unknown): data is Record<string, unknown> {
  const d = data as { type?: string } | null;
  return Boolean(d && d.type === PREFETCH_SW_RESULT_TYPE);
}

/** Слушаем window + ServiceWorkerContainer + опционально MessageChannel. */
function createSwPrefetchResultWaiter(timeoutMs: number): SwPrefetchWaiter {
  if (typeof window === "undefined") {
    return {
      promise: Promise.resolve(null),
      attachReplyPort: () => {},
    };
  }

  let settled = false;
  let resolveOuter: (value: Record<string, unknown> | null) => void = () => {};
  const promise = new Promise<Record<string, unknown> | null>((resolve) => {
    resolveOuter = resolve;
  });

  const finish = (data: Record<string, unknown> | null) => {
    if (settled) return;
    settled = true;
    cleanup();
    resolveOuter(data);
  };

  const onResult = (data: unknown) => {
    if (isPrefetchResultMessage(data)) {
      finish(data);
    }
  };

  const onWindowMsg = (ev: MessageEvent) => onResult(ev.data);
  const onContainerMsg = (ev: MessageEvent) => onResult(ev.data);

  window.addEventListener("message", onWindowMsg);
  if ("serviceWorker" in navigator) {
    navigator.serviceWorker.addEventListener("message", onContainerMsg);
  }

  const timer = window.setTimeout(() => finish(null), timeoutMs);

  const cleanup = () => {
    window.clearTimeout(timer);
    window.removeEventListener("message", onWindowMsg);
    if ("serviceWorker" in navigator) {
      navigator.serviceWorker.removeEventListener("message", onContainerMsg);
    }
  };

  return {
    promise,
    attachReplyPort(port: MessagePort) {
      port.onmessage = (ev: MessageEvent) => onResult(ev.data);
      if (typeof port.start === "function") {
        port.start();
      }
    },
  };
}

type SwPostPrefetchMeta = {
  postMode: string;
  hadController: boolean;
  replyPort: MessagePort | null;
};

async function postPrefetchToServiceWorker(urls: string[]): Promise<SwPostPrefetchMeta> {
  if (!("serviceWorker" in navigator)) {
    return { postMode: "no_sw", hadController: false, replyPort: null };
  }

  const payload: PrefetchPayload = { type: "WW_PREFETCH_URLS", urls };
  const hadController = Boolean(navigator.serviceWorker.controller);
  let postMode = "none";

  const channel = typeof MessageChannel !== "undefined" ? new MessageChannel() : null;

  let target: ServiceWorker | null = navigator.serviceWorker.controller;
  let targetLabel = "controller";

  if (!target) {
    try {
      const reg = await navigator.serviceWorker.ready;
      if (reg.active) {
        target = reg.active;
        targetLabel = "active";
      } else if (reg.waiting) {
        target = reg.waiting;
        targetLabel = "waiting";
      } else if (reg.installing) {
        target = reg.installing;
        targetLabel = "installing";
      }
    } catch {
      // ignore
    }
  }

  if (target) {
    try {
      if (channel) {
        target.postMessage(payload, [channel.port2]);
        postMode = `${targetLabel}+channel`;
      } else {
        target.postMessage(payload);
        postMode = targetLabel;
      }
    } catch {
      postMode = `${targetLabel}_post_failed`;
    }
  }

  return {
    postMode,
    hadController,
    replyPort: channel?.port1 ?? null,
  };
}

function toAbsoluteUrl(path: string): string | null {
  const raw = String(path || "").trim();
  if (!raw) return null;
  try {
    return new URL(raw, window.location.origin).toString();
  } catch {
    return null;
  }
}

function lessonStartIndex(section: ISection, round: number): number {
  const shed = section.UserLessonsScheduled.find((s) => Number(s.round) === Number(round));
  if (!shed) return 0;

  const today = new Date();
  const targetDate = new Date(shed.start_date);
  const h = today.getTimezoneOffset() / 60;
  targetDate.setUTCHours(TIME_HOURS_LS + h, TIME_MINUTE_LS, 0, 0);
  const diff = Math.floor(
    Math.abs((today.getTime() - targetDate.getTime()) / (1000 * 3600 * 24))
  );
  return Math.max(0, diff);
}

function getCurrentAndNextLessons(sections: ISection[]): ILesson[] {
  const allSchedules = sections
    .flatMap((s) => s.UserLessonsScheduled || [])
    .sort((a, b) => +new Date(a.start_date) - +new Date(b.start_date));
  if (allSchedules.length === 0) return [];

  const now = new Date();
  let current = allSchedules[allSchedules.length - 1];
  for (let i = 0; i < allSchedules.length; i++) {
    const d = new Date(allSchedules[i].start_date);
    const h = now.getTimezoneOffset() / 60;
    d.setUTCHours(TIME_HOURS_LS + h, TIME_MINUTE_LS, 0, 0);
    if (+d > +now) {
      current = allSchedules[Math.max(0, i - 1)];
      break;
    }
  }

  const section = sections.find((s) => s.id === current.SectionId);
  if (!section) return [];
  const roundLessons = (section.Lesson_SectionRelation || [])
    .filter((l) => Number(l.round) === Number(current.round))
    .sort((a, b) => a.sequence_number__lesson - b.sequence_number__lesson);
  if (roundLessons.length === 0) return [];

  const start = lessonStartIndex(section, current.round);
  return roundLessons.slice(start, start + 4);
}

function buildPrefetchUrls(lessons: ILesson[]): string[] {
  const urls = new Set<string>();
  for (const lesson of lessons) {
    urls.add(`/section/${lesson.section_id}/lesson/${lesson.id}`);
    for (const ex of lesson.Exercise_LessonRelation || []) {
      const mediaCandidates = [ex.url, ex.previwUrl];
      for (const m of mediaCandidates) {
        const abs = toAbsoluteUrl(m);
        if (!abs) continue;
        if (abs.startsWith(window.location.origin)) {
          urls.add(abs.replace(window.location.origin, ""));
        } else {
          urls.add(abs);
        }
      }
    }
  }
  return Array.from(urls);
}

async function dispatchPrefetch(urls: string[]): Promise<string[]> {
  if (urls.length === 0) return [];

  const waiter = createSwPrefetchResultWaiter(PREFETCH_SW_WAIT_MS);
  const swPost = await postPrefetchToServiceWorker(urls);
  if (swPost.replyPort) {
    waiter.attachReplyPort(swPost.replyPort);
  }
  const swSummaryPromise = waiter.promise;

  // 2) Fallback: сетевой прогрев (часть ответов подхватит SW fetch handler).
  const fallbackResults = await Promise.all(
    urls.map((u) =>
      fetch(u, {
        method: "GET",
        credentials: "include",
        cache: "no-store",
      })
        .then((resp) => ({ ok: resp.ok, status: resp.status }))
        .catch(() => ({ ok: false, status: -1 }))
    )
  );
  const fallbackOk = fallbackResults.filter((x) => x.ok).length;
  const fallbackFailed = fallbackResults.length - fallbackOk;

  const swRaw = await swSummaryPromise;
  const swSummary =
    swRaw && typeof swRaw === "object"
      ? {
          attempted: swRaw.attempted,
          cached: swRaw.cached,
          failed: swRaw.failed,
          skipped: swRaw.skipped,
        }
      : null;

  try {
    logClientDiagnostic({
      action: "offline.prefetch_telemetry",
      description: "Prefetch SW summary + fallback GET outcomes",
      endpoint: "/miniapi/get_current_lessons",
      method: "POST",
      additionalData: {
        urlsCount: urls.length,
        fallbackOk,
        fallbackFailed,
        swSummary,
        swPostMode: swPost.postMode,
        swHadController: swPost.hadController,
        swWaitMs: PREFETCH_SW_WAIT_MS,
      },
    });
  } catch {
    /* ignore */
  }

  return urls;
}

/** Только для юнит-тестов телеметрии prefetch (не использовать в приложении). */
export const _dispatchPrefetchForTests = dispatchPrefetch;

function getFromCurrentLessonAndNext3(
  sections: ISection[],
  sectionId: number,
  lessonId: number
): ILesson[] {
  const section = sections.find((s) => Number(s.id) === Number(sectionId));
  if (!section) return [];
  const all = (section.Lesson_SectionRelation || []).slice();
  const current = all.find((l) => Number(l.id) === Number(lessonId));
  if (!current) return [];
  const round = Number(current.round);
  const roundLessons = all
    .filter((l) => Number(l.round) === round)
    .sort((a, b) => Number(a.sequence_number__lesson) - Number(b.sequence_number__lesson));
  const idx = roundLessons.findIndex((l) => Number(l.id) === Number(lessonId));
  if (idx < 0) return [];
  return roundLessons.slice(idx, idx + 4);
}

export async function prefetchCurrentAndNextLessons(sections: ISection[]): Promise<string[]> {
  if (typeof window === "undefined" || sections.length === 0) return [];
  const lessons = getCurrentAndNextLessons(sections);
  const urls = buildPrefetchUrls(lessons);
  return dispatchPrefetch(urls);
}

export async function prefetchFromCurrentLesson(
  sections: ISection[],
  sectionId: number,
  lessonId: number
): Promise<string[]> {
  if (typeof window === "undefined" || sections.length === 0) return [];
  const lessons = getFromCurrentLessonAndNext3(sections, sectionId, lessonId);
  const urls = buildPrefetchUrls(lessons);
  return dispatchPrefetch(urls);
}

